/**
 * Constants related to PostHog feature flags.
 *
 * This file contains the definitions of feature flags used throughout the application
 * for feature toggling and experimentation.
 *
 * Flags are organized by category for easier navigation and maintenance.
 *
 * @module posthog/constants
 */

/**
 * Feature flags available in PostHog.
 * Organized by functional category.
 *
 * @enum {string}
 */
export enum PostHogFeatureFlag {
  // ============================================================================
  // OUTPUT & PRODUCTION CONTROLS
  // ============================================================================

  /**
   * First-run onboarding funnel (ENG-3796) — the welcome screen, bridge
   * detection, camera add wizard, and everything downstream of them.
   *
   * Fails CLOSED. An environment where this flag is absent, false, or where
   * PostHog is unreachable behaves exactly as it did before the funnel existed:
   * the app renders straight through and nothing is written to the onboarding
   * flag.
   */
  ONBOARDING_FUNNEL = 'onboarding-funnel',

  /** Recording functionality - controls recording button and settings panel */
  RECORDING_FEATURE = 'recording-feature',

  /** NDI Output functionality - controls NDI output tab and settings */
  NDI_OUTPUT_FEATURE = 'ndi-output-feature',

  /** RTMP streaming capability - controls RTMP output tab and streaming */
  RTMP_OUTPUT_FEATURE = 'rtmp-egress', // Note: keeping 'rtmp-egress' value for backward compatibility with existing PostHog flag

  // ============================================================================
  // EARLY ACCESS FEATURES
  // ============================================================================

  /** Early access opt-in for Recording (includes ISO Recording) */
  EARLY_ACCESS_RECORDING = RECORDING_FEATURE,

  /** Early access opt-in for Output (NDI Output + RTMP Streaming) */
  EARLY_ACCESS_OUTPUT = NDI_OUTPUT_FEATURE,

  /** Early access opt-in for RTMP streaming */
  EARLY_ACCESS_RTMP = RTMP_OUTPUT_FEATURE,

  // ============================================================================
  // SOURCE DISCOVERY & MANAGEMENT
  // ============================================================================

  /** USB/PCIe video device discovery - controls USB video sources in discovery */
  USB_VIDEO_SOURCES_FEATURE = 'usb-video-sources-feature',

  /** Extended Device Discovery (mDNS fingerprint matching + Brand column + 1-click add). Default OFF. */
  EXTENDED_DISCOVERY = 'extended-discovery',

  /** Bridge features - controls bridge selection, linking, and management */
  BRIDGE_FEATURE = 'bridge-feature',

  // Multi-Add Phase 1 feature gate. Off by default; PostHog dashboard controls rollout.
  /** Bulk multi-select add for discovered sources (ONVIF, NDI, RTSP, Local) */
  BULK_ADD_SOURCES_FEATURE = 'bulk-add-sources-feature',
  /** Additional-Bridge workflows, independent from the base Bridge lifecycle */
  MULTIBRIDGE_FEATURE = 'multibridge-feature',

  // ============================================================================
  // UI FEATURES & SETTINGS
  // ============================================================================

  /** Organizations functionality - controls organizations section in settings */
  ORGANIZATIONS_FEATURE = 'organizations-feature',

  /** Studio selector - controls studio dropdown in account settings */
  STUDIO_SELECTOR_FEATURE = 'studio-selector-feature',

  /** Studio hostname display - controls whether to show hostname instead of room name */
  // NOT CURRENTLY BEING USED
  STUDIO_HOSTNAME_DISPLAY = 'studio-hostname-display',

  /** Driver badge mode - controls driver stability badge display */
  DRIVER_BADGE_FEATURE = 'driver-badge-feature',

  /** Enables debug UI components in non-production environments */
  STUDIO_UI_DEBUGGING = 'studio-ui-debugging',

  /**
   * Email-verification gate (ENG-3797). Gates the post-signup app shell until
   * the user's Auth0 `email_verified` claim is true. DEFAULT OFF — callers
   * must pass `{ defaultValue: false }` so loading/error resolves to OFF.
   */
  EMAIL_VERIFICATION_GATE = 'email-verification-gate',

  // ============================================================================
  // CAMERA CONTROL FEATURES
  // ============================================================================

  /** DPAD default state - controls DPAD initial collapsed/expanded state */
  DPAD_DEFAULT_COLLAPSED_FEATURE = 'dpad-default-collapsed-feature',

  /** Variable speed control - controls speed slider visibility */
  VARIABLE_SPEED_CONTROL_FEATURE = 'variable-speed-control-feature',

  /** Camera auto-tracking feature */
  AUTOTRACKING = 'autotracking',

  /** Debug mode for auto-tracking */
  AUTOTRACKING_DEBUG = 'autotracking-debug',

  /** Opt-in gate for the encoder's inverted-display correction (off by default) */
  ENABLE_DISPLAY_INVERSION = 'enable-display-inversion',

  // ============================================================================
  // SIGNUP & ONBOARDING
  // ============================================================================

  /** Prequal wizard on /register — gates the 4-step questionnaire before Auth0 signup */
  PREQUAL_WIZARD = 'prequal-wizard',

  // ============================================================================
  // NETWORK & STABILITY
  // ============================================================================

  /**
   * Bandwidth governor — enforces per-session upload limits to prevent
   * network saturation in constrained environments (e.g. shared uplinks).
   * Controls the bandwidth-limiting subsystem in the encoder pipeline.
   */
  BANDWIDTH_GOVERNOR = 'bandwidth-governor',

  // ============================================================================
  // PERFORMANCE & MONITORING
  // ============================================================================

  /** React performance monitoring with react-scan */
  REACT_SCAN_MONITORING = 'react-scan-monitoring',

  /** Enables bounded forwarding of known-noisy PostHog error classes */
  ERROR_TRACKING_VERBOSE = 'posthog-error-tracking-verbose',

  /** Republishes the autocapture clicks the onboarding hygiene filter suppresses */
  AUTOCAPTURE_HYGIENE_VERBOSE = 'posthog-autocapture-hygiene-verbose'
}

/**
 * Feature flag categories for organized access and bulk operations.
 * Use these when you need to check multiple flags in a category.
 */
export const FeatureFlagCategories = {
  /**
   * Output and production control flags.
   * @example
   * ```ts
   * const outputFlags = FeatureFlagCategories.OUTPUT;
   * const allEnabled = outputFlags.every(flag => manager.getFeatureFlag(flag));
   * ```
   */
  OUTPUT: [
    PostHogFeatureFlag.RECORDING_FEATURE,
    PostHogFeatureFlag.NDI_OUTPUT_FEATURE,
    PostHogFeatureFlag.RTMP_OUTPUT_FEATURE
  ] as const,

  /** Early access opt-in flags for beta features */
  EARLY_ACCESS: [
    PostHogFeatureFlag.EARLY_ACCESS_RECORDING,
    PostHogFeatureFlag.EARLY_ACCESS_OUTPUT
  ] as const,

  /** Source discovery and management flags */
  SOURCE_DISCOVERY: [
    PostHogFeatureFlag.USB_VIDEO_SOURCES_FEATURE,
    PostHogFeatureFlag.BRIDGE_FEATURE,
    // Included so bulk-category checks cover BULK_ADD_SOURCES_FEATURE.
    PostHogFeatureFlag.BULK_ADD_SOURCES_FEATURE,
    PostHogFeatureFlag.MULTIBRIDGE_FEATURE
  ] as const,

  /** UI features and settings flags */
  UI_FEATURES: [
    PostHogFeatureFlag.ORGANIZATIONS_FEATURE,
    PostHogFeatureFlag.STUDIO_SELECTOR_FEATURE,
    PostHogFeatureFlag.STUDIO_HOSTNAME_DISPLAY,
    PostHogFeatureFlag.DRIVER_BADGE_FEATURE,
    PostHogFeatureFlag.STUDIO_UI_DEBUGGING
  ] as const,

  /** Camera control interface flags */
  CAMERA_CONTROL: [
    PostHogFeatureFlag.DPAD_DEFAULT_COLLAPSED_FEATURE,
    PostHogFeatureFlag.VARIABLE_SPEED_CONTROL_FEATURE,
    PostHogFeatureFlag.AUTOTRACKING,
    PostHogFeatureFlag.AUTOTRACKING_DEBUG,
    PostHogFeatureFlag.ENABLE_DISPLAY_INVERSION
  ] as const,

  /**
   * Network and stability flags.
   * Controls subsystems that govern bandwidth usage and connection resilience.
   */
  NETWORK: [PostHogFeatureFlag.BANDWIDTH_GOVERNOR] as const,

  /** Performance and monitoring flags */
  PERFORMANCE: [
    PostHogFeatureFlag.REACT_SCAN_MONITORING,
    PostHogFeatureFlag.ERROR_TRACKING_VERBOSE
  ] as const
} as const;

/** Capabilities whose approved unavailable/missing-flag behavior is OFF. */
const FAIL_CLOSED_CAPABILITY_FLAGS = new Set<string>([
  PostHogFeatureFlag.RECORDING_FEATURE,
  PostHogFeatureFlag.NDI_OUTPUT_FEATURE,
  PostHogFeatureFlag.RTMP_OUTPUT_FEATURE,
  PostHogFeatureFlag.USB_VIDEO_SOURCES_FEATURE,
  PostHogFeatureFlag.BRIDGE_FEATURE,
  PostHogFeatureFlag.MULTIBRIDGE_FEATURE,
  PostHogFeatureFlag.ORGANIZATIONS_FEATURE,
  PostHogFeatureFlag.AUTOTRACKING
]);

/** Whether a flag participates in the approved fail-closed capability contract. */
export const isFailClosedCapabilityFlag = (flag: string): boolean =>
  FAIL_CLOSED_CAPABILITY_FLAGS.has(flag);

/** Resolve the value used only when PostHog cannot provide a real flag value. */
export const resolveFeatureFlagFallback = (flag: string, requestedDefault?: boolean): boolean => {
  // Per enforcement-layer policy: rollout controls fail OFF when the flag is
  // absent or PostHog is unavailable, even if a legacy caller requested true.
  if (isFailClosedCapabilityFlag(flag)) {
    return false;
  }

  return requestedDefault ?? false;
};

/**
 * Resolve the current project contract for linking an additional Bridge.
 *
 * PostHog controls rollout availability, but cannot authorize Iris MultiBridge:
 * a forged flag, direct call, or stale persisted value must still fail closed at
 * every action boundary.
 */
export const isMultiBridgeAvailable = (
  vendor: string | undefined,
  bridgeEnabled: boolean | undefined,
  multiBridgeEnabled: boolean | undefined
): boolean => vendor === 'ptzoptics' && bridgeEnabled === true && multiBridgeEnabled === true;

export type BridgeLinkDenyReason =
  | 'unknown_vendor'
  | 'bridge_disabled'
  | 'iris_single_bridge'
  | 'multibridge_disabled';

export type BridgeLinkDecision =
  | { readonly allowed: true }
  | { readonly allowed: false; readonly reason: BridgeLinkDenyReason };

/**
 * Evaluate a Bridge-link operation and retain the reason when it is denied.
 * Callers log this result at action boundaries so direct and stale-state
 * attempts remain diagnosable without weakening the product contract.
 */
export const evaluateBridgeLink = (
  vendor: string | undefined,
  hasExistingBridge: boolean,
  bridgeEnabled: boolean | undefined,
  multiBridgeEnabled: boolean | undefined
): BridgeLinkDecision => {
  if (vendor !== 'iris' && vendor !== 'ptzoptics') {
    return { allowed: false, reason: 'unknown_vendor' };
  }

  if (bridgeEnabled !== true) {
    return { allowed: false, reason: 'bridge_disabled' };
  }

  if (!hasExistingBridge) {
    return { allowed: true };
  }

  if (vendor === 'iris') {
    return { allowed: false, reason: 'iris_single_bridge' };
  }

  if (multiBridgeEnabled !== true) {
    return { allowed: false, reason: 'multibridge_disabled' };
  }

  return { allowed: true };
};

/**
 * Enforce the complete Bridge-link action boundary for the current contract.
 *
 * A known Iris or Hive runtime may link its first Bridge when the base rollout
 * flag is ON. Once a Bridge already exists, only Hive with both flags ON may
 * start another link. The same decision is used by rendered controls and
 * direct/event-driven entry points so stale UI cannot widen the contract.
 */
export const canStartBridgeLink = (
  vendor: string | undefined,
  hasExistingBridge: boolean,
  bridgeEnabled: boolean | undefined,
  multiBridgeEnabled: boolean | undefined
): boolean =>
  evaluateBridgeLink(vendor, hasExistingBridge, bridgeEnabled, multiBridgeEnabled).allowed;

/**
 * Helper functions for working with feature flag categories.
 */
export const FeatureFlagHelpers = {
  /**
   * Check if a flag belongs to a specific category.
   *
   * @example
   * ```ts
   * if (FeatureFlagHelpers.isInCategory(flag, 'OUTPUT')) {
   *   // Handle output-related flag
   * }
   * ```
   */
  isInCategory(flag: PostHogFeatureFlag, category: keyof typeof FeatureFlagCategories): boolean {
    return (FeatureFlagCategories[category] as readonly PostHogFeatureFlag[]).includes(flag);
  },

  /**
   * Get the category name for a given flag.
   * Returns undefined if flag is not found in any category.
   */
  getCategoryForFlag(flag: PostHogFeatureFlag): keyof typeof FeatureFlagCategories | undefined {
    for (const [category, flags] of Object.entries(FeatureFlagCategories)) {
      if ((flags as readonly PostHogFeatureFlag[]).includes(flag)) {
        return category as keyof typeof FeatureFlagCategories;
      }
    }
    return undefined;
  },

  /**
   * Get all flags in a category.
   */
  getFlagsInCategory(category: keyof typeof FeatureFlagCategories): readonly PostHogFeatureFlag[] {
    return FeatureFlagCategories[category];
  }
};
